import { NgClass, NgStyle } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, effect, inject } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { TilbyGesturesDirective } from "@tilby/tilby-ui-lib/directives/tilby-gestures";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { ConfigurationManagerService, ScreenOrientationService } from "src/app/core";
import { CashregisterStateService } from "src/app/features/cashregister";
import { keyBy } from "src/app/shared/utils";
import { Items } from "tilby-models";
import { ShowcaseStockBadgeComponent } from "../showcase-stock-badge";

export type ShowcaseCardLayout = 'card' | 'tab';

@Component({
    selector: "app-showcase-grid-v2",
    templateUrl: "./showcase-grid-v2.component.html",
    standalone: true,
    imports: [NgClass, NgStyle, MatIconModule, ShowcaseStockBadgeComponent, TilbyCurrencyPipe, TilbyGesturesDirective]
})
export class ShowcaseGridV2Component implements OnChanges, AfterViewInit {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly cashregisterStateService = inject(CashregisterStateService);
    private readonly screenOrientationService = inject(ScreenOrientationService);

    private static readonly defaultColor = "BBDEFB";

    @Input() itemsSet!: Items[];
    @Input() gridCols = 4;
    @Input() layout: ShowcaseCardLayout = 'card';
    @Input() showThumbnail = true;
    @Input() displayMode = "normal";
    @Input() customClass = '';
    @Input() fontNameSize = 12;
    @Input() fontPriceSize = 12;
    @Input() showItemIcon = false;
    @Input() getStockStatus: Function = () => {};
    @Input() set priceList(value: number) {
        this.priceIdentifier = `price${value}` as keyof Items;
    }
    @Output() onItemTap = new EventEmitter<Items>();
    @Output() onItemPress = new EventEmitter<Items>();

    @ViewChild('grid', { static: true }) private readonly gridRef!: ElementRef<HTMLElement>;

    protected priceIdentifier: keyof Items = "price1";
    protected footerStacked = false;
    private gridWidth = 0;
    private maxPriceChars = 8;
    private hasStockBadge = false;
    private maxQuantityChars = 0;
    protected readonly showPrices = this.configurationManagerService.isUserPermitted('show_prices_ui');
    private readonly categoriesById = keyBy(this.cashregisterStateService.categories, (c) => c.id);

    private static readonly CARD_BORDER = 2;
    private static readonly CARD_PADDING = 8;
    private static readonly CARD_GAP = 4;
    private static readonly LINE_HEIGHT = 1.2;
    private static readonly STOCK_BADGE_HEIGHT = 28;
    private static readonly STOCK_BADGE_WIDTH = 28;
    private static readonly STOCK_BADGE_QUANTITY_CHROME = 38;
    private static readonly STOCK_QUANTITY_FONT_SIZE = 12;
    private static readonly FOOTER_GAP = 8;
    private static readonly GRID_GAP = 8;
    private static readonly GRID_PADDING = 4;
    private static readonly CHAR_WIDTH_RATIO = 0.6;
    private static readonly ORIENTATION_SETTLE_MS = 300;

    constructor() {
        let isFirstRun = true;
        effect(() => {
            this.screenOrientationService.getOrientation();
            if (isFirstRun) {
                isFirstRun = false;
                return;
            }
            setTimeout(() => this.measureGrid(), ShowcaseGridV2Component.ORIENTATION_SETTLE_MS);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemsSet || changes.getStockStatus || changes.priceList) {
            this.updateFooterStats();
        }
        this.updateFooterLayout();
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.measureGrid());
    }

    private measureGrid(): void {
        this.gridWidth = this.gridRef.nativeElement.clientWidth;
        this.updateFooterLayout();
    }

    private updateFooterStats(): void {
        const items = this.itemsSet ?? [];
        this.hasStockBadge = false;
        this.maxQuantityChars = 0;
        this.maxPriceChars = this.showPrices ? 4 : 3;
        for (const item of items) {
            const stock = this.getStockStatus(item);
            if (stock && stock.status !== 'stock-disabled') {
                this.hasStockBadge = true;
                if (stock.stock_quantity !== undefined && stock.stock_quantity !== null) {
                    this.maxQuantityChars = Math.max(this.maxQuantityChars, String(stock.stock_quantity).length + (stock.stock_quantity > 0 ? 1 : 0));
                }
            }
            if (this.showPrices) {
                const price = Number(item[this.priceIdentifier]);
                this.maxPriceChars = Math.max(this.maxPriceChars, (Number.isFinite(price) ? price.toFixed(2).length : 4) + 2);
            }
        }
    }

    private updateFooterLayout(): void {
        if (!this.gridWidth || !this.hasStockBadge) {
            this.footerStacked = false;
            return;
        }
        const { GRID_GAP, GRID_PADDING, CARD_BORDER, CARD_PADDING, FOOTER_GAP, CHAR_WIDTH_RATIO } = ShowcaseGridV2Component;
        const columnWidth = (this.gridWidth - GRID_PADDING - GRID_GAP * (this.gridCols - 1)) / this.gridCols;
        const accentWidth = 8;
        const available = columnWidth - CARD_BORDER - accentWidth - CARD_PADDING - 2 * 4;
        const { STOCK_BADGE_QUANTITY_CHROME, STOCK_QUANTITY_FONT_SIZE } = ShowcaseGridV2Component;
        const badgeWidth = this.maxQuantityChars
            ? STOCK_BADGE_QUANTITY_CHROME + this.maxQuantityChars * CHAR_WIDTH_RATIO * STOCK_QUANTITY_FONT_SIZE
            : ShowcaseGridV2Component.STOCK_BADGE_WIDTH;
        const needed = this.maxPriceChars * CHAR_WIDTH_RATIO * this.fontPriceSize + FOOTER_GAP + badgeWidth;
        this.footerStacked = available < needed;
    }

    protected get compact(): boolean {
        return this.displayMode === 'compact';
    }

    /** Righe riservate al nome: lo spazio resta lo stesso qualunque sia la lunghezza del testo. */
    protected get nameLines(): number {
        return this.compact && this.layout !== 'tab' ? 1 : 3;
    }

    /** Altezza fissa della foto nel layout card. */
    protected get imageHeight(): number {
        return this.compact ? 96 : 106;
    }

    private get sectionPadding(): number {
        return this.compact ? 4 : 8;
    }

    /** Lato dell'icona articolo (mostrata solo nel layout card). */
    protected get iconSize(): number {
        return this.compact ? 24 : 32;
    }

    protected get nameBlockHeight(): number {
        const text = this.nameLines * ShowcaseGridV2Component.LINE_HEIGHT * this.fontNameSize;
        const thumb = this.layout === 'tab' ? (this.compact ? 48 : 60) : 0;
        const icon = this.layout === 'card' && this.showItemIcon ? this.iconSize : 0;
        return Math.ceil(Math.max(text, thumb, icon) + this.sectionPadding);
    }

    private get footerHeight(): number {
        const priceLine = ShowcaseGridV2Component.LINE_HEIGHT * this.fontPriceSize;
        const content = this.footerStacked
            ? priceLine + 2 + ShowcaseGridV2Component.STOCK_BADGE_HEIGHT
            : Math.max(ShowcaseGridV2Component.STOCK_BADGE_HEIGHT, priceLine);
        return content + this.sectionPadding;
    }

    protected get rowHeight(): number {
        const { CARD_BORDER, CARD_PADDING, CARD_GAP } = ShowcaseGridV2Component;
        const hasImage = this.layout === 'card' && this.showThumbnail;
        return Math.ceil(
            CARD_BORDER + CARD_PADDING
            + (hasImage ? this.imageHeight + CARD_GAP : 0)
            + this.nameBlockHeight + CARD_GAP + this.footerHeight
        );
    }

    protected itemTap(item: Items): void {
        this.onItemTap.emit(item);
    }

    protected itemPress(item: Items): void {
        this.onItemPress.emit(item);
    }

    protected accentColor(item: Items): string {
        return `#${item.color || this.categoriesById[item.category_id!]?.color || ShowcaseGridV2Component.defaultColor}`;
    }

    protected initials(item: Items): string {
        return (item.name ?? '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((word) => word[0])
            .join('')
            .toUpperCase();
    }
}
